// Phase 2.7 — KPI derivations + memo'd filtered/summary lists extracted from
// MonthlyAccounting.jsx. Pure derivation, no fetching — the orchestrator
// still owns the network calls (load) and just passes data + monthFilter in.

import { useMemo } from "react";

export default function useMonthlyAccounting(data, monthFilter) {
  const summary = useMemo(() => data.summary || [], [data]);
  const filtered = useMemo(
    () => (monthFilter ? summary.filter((s) => s.month === monthFilter) : summary),
    [summary, monthFilter],
  );

  // KPIs respect the active month filter — when the operator picks a single
  // month, all four cards (Gross, Expenses, Net, NK/RR settlements) recompute
  // for that month only. With no filter, they're the FY-wide totals.
  const kpis = useMemo(() => {
    const kpiInc = filtered.reduce((a, s) => a + (s.commission_received || 0), 0);
    const kpiExp = filtered.reduce((a, s) => a + (s.total_expense || 0), 0);
    const kpiNet = kpiInc - kpiExp;
    const kpiNkOutstanding = filtered.reduce((a, s) => a + Math.max(s.nk_outstanding || 0, 0), 0);
    const kpiRrOutstanding = filtered.reduce((a, s) => a + Math.max(s.rr_outstanding || 0, 0), 0);
    const kpiNkOpenMonths = filtered.filter((s) => (s.nk_outstanding || 0) > 1).length;
    const kpiRrOpenMonths = filtered.filter((s) => (s.rr_outstanding || 0) > 1).length;
    return {
      kpiInc, kpiExp, kpiNet,
      kpiNkOutstanding, kpiRrOutstanding,
      kpiNkOpenMonths, kpiRrOpenMonths,
    };
  }, [filtered]);

  // Build the "Filter by month" dropdown options off the loaded summary so we
  // never offer a month that has no rows.
  const availableMonths = useMemo(() => {
    const ms = (summary || []).map((s) => s.month).filter(Boolean);
    return Array.from(new Set(ms)).sort().reverse();
  }, [summary]);

  const monthExpenses = useMemo(() => {
    if (monthFilter) return (data.expenses || []).filter((e) => e.month === monthFilter);
    return data.expenses || [];
  }, [data.expenses, monthFilter]);

  return { summary, filtered, kpis, availableMonths, monthExpenses };
}
